<script lang="ts">
  import { Plus, Trash2 } from '@lucide/svelte';
  import { WORKING_SHIFTS_MAX, type WorkingShifts, WorkingShiftsSchema } from '@clinic/contracts';
  import { useOrg } from '../../../shared/auth/org.svelte';
  import { fa } from '../../../shared/i18n/fa';
  import { online } from '../../../shared/lib/online.svelte';
  import { Alert, Button, ErrorAlert, Field, Input, Select } from '../../../shared/ui';
  import { useSaveBookingSettings } from '../../scheduling';
  import {
    DAY_END,
    HOURS_STEP,
    addShift,
    cloneShifts,
    copyToAll,
    formatClock,
    hoursProblems,
    nameProblems,
    nextShiftName,
    removeShift,
    sameShifts,
    timeOptions,
    withDay,
  } from '../lib/hours';

  let { initial, readOnly = false, onSaved }: { initial: WorkingShifts; readOnly?: boolean; onSaved?: () => void } = $props();

  const org = useOrg();
  const save = useSaveBookingSettings(org.orgId);
  // The editor starts from the saved shifts and keeps the admin's edits until saved.
  // svelte-ignore state_referenced_locally
  let draft = $state<WorkingShifts>(cloneShifts(initial));
  let saved = $state(false);
  const nameIssues = $derived(nameProblems(draft));
  const dayIssues = $derived(draft.map((s) => hoursProblems(s.days)));
  const changed = $derived(!sameShifts(draft, initial));
  const invalid = $derived(nameIssues.some((p) => p !== null) || dayIssues.some((l) => l.some((p) => p !== null)));
  const FROM = timeOptions(0, DAY_END - HOURS_STEP);
  const TO = timeOptions(HOURS_STEP, DAY_END);
  const many = $derived(draft.length > 1);
  const full = $derived(draft.length >= WORKING_SHIFTS_MAX);

  function touch(next: WorkingShifts) {
    saved = false;
    draft = next;
  }
  function editDay(shift: number, day: number, change: Partial<WorkingShifts[number]['days'][number]>) {
    touch(draft.map((s, i) => (i === shift ? { name: s.name, days: withDay(s.days, day, change) } : s)));
  }
  function rename(shift: number, name: string) {
    touch(draft.map((s, i) => (i === shift ? { name, days: s.days } : s)));
  }
  const label = (shift: number, day: string) => (many ? fa.workingHours.inShift(day, draft[shift]!.name) : day);

  function submit(e: SubmitEvent) {
    e.preventDefault();
    saved = false;
    const parsed = WorkingShiftsSchema.safeParse(draft.map((s) => ({ name: s.name.trim(), days: s.days })));
    if (!parsed.success || invalid) return;
    save.mutate(
      { workingShifts: parsed.data },
      {
        onSuccess: () => {
          saved = true;
          onSaved?.();
        },
      },
    );
  }
</script>

<form onsubmit={submit} novalidate class="flex flex-col gap-4" data-testid="working-hours">
  {#each draft as shift, si (si)}
    {@const source = shift.days.findIndex((d) => d.open)}
    {@const nameIssue = nameIssues[si]}
    <section class="flex flex-col gap-2" aria-label={shift.name} data-testid="working-shift">
      <div class="flex flex-wrap items-end gap-2">
        <div class="min-w-48 flex-1">
          <Field label={fa.workingHours.shiftName} hint={fa.workingHours.shiftNameHint} error={nameIssue ? fa.workingHours.nameProblems[nameIssue] : undefined}>
            {#snippet children(a11y)}
              <Input
                {...a11y}
                value={shift.name}
                maxlength={40}
                disabled={readOnly || !online.value}
                oninput={(e) => rename(si, e.currentTarget.value)}
              />
            {/snippet}
          </Field>
        </div>
        {#if !readOnly && many}
          <Button
            variant="secondary"
            size="sm"
            disabled={!online.value}
            aria-label={fa.workingHours.removeShift(shift.name)}
            onclick={() => touch(removeShift(draft, si))}><Trash2 class="size-4" aria-hidden="true" /></Button
          >
        {/if}
      </div>
      {#if !readOnly && source >= 0}
        <Button
          variant="secondary"
          size="sm"
          class="self-start"
          disabled={!online.value}
          title={fa.workingHours.copyHint}
          onclick={() => touch(draft.map((s, i) => (i === si ? { name: s.name, days: copyToAll(s.days, source) } : s)))}
          >{fa.workingHours.copyToAll}</Button
        >
      {/if}
      <ul class="flex flex-col divide-y divide-hairline">
        {#each fa.workingHours.days as name, i (name)}
          {@const day = shift.days[i]!}
          {@const problem = dayIssues[si]![i]}
          <li class="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
            <label class="flex min-h-11 w-28 cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                role="switch"
                class="size-5 accent-[var(--accent-solid)]"
                checked={day.open}
                disabled={readOnly || !online.value}
                aria-label={fa.workingHours.openAria(label(si, name))}
                onchange={(e) => editDay(si, i, { open: e.currentTarget.checked })}
              />
              <span class="font-medium">{name}</span>
            </label>
            {#if day.open}
              <div class="flex flex-wrap items-center gap-2">
                <Select
                  class="w-28"
                  aria-label={fa.workingHours.fromAria(label(si, name))}
                  aria-invalid={problem ? true : undefined}
                  disabled={readOnly || !online.value}
                  value={String(day.from)}
                  onchange={(e) => editDay(si, i, { from: Number(e.currentTarget.value) })}
                >
                  {#each FROM as m (m)}<option value={String(m)}>{formatClock(m)}</option>{/each}
                </Select>
                <span class="text-sm text-text-3">{fa.workingHours.until}</span>
                <Select
                  class="w-28"
                  aria-label={fa.workingHours.toAria(label(si, name))}
                  aria-invalid={problem ? true : undefined}
                  disabled={readOnly || !online.value}
                  value={String(day.to)}
                  onchange={(e) => editDay(si, i, { to: Number(e.currentTarget.value) })}
                >
                  {#each TO as m (m)}<option value={String(m)}>{formatClock(m)}</option>{/each}
                </Select>
              </div>
            {:else}
              <span class="text-sm text-text-3">{fa.workingHours.closed}</span>
            {/if}
            {#if problem}<p role="alert" class="w-full text-sm text-danger">{fa.workingHours.problems[problem]}</p>{/if}
          </li>
        {/each}
      </ul>
    </section>
  {/each}
  {#if !readOnly}
    <div class="flex flex-col gap-1">
      <Button
        variant="secondary"
        class="w-full sm:w-auto sm:self-start"
        disabled={!online.value || full}
        onclick={() => touch(addShift(draft, nextShiftName(draft, fa.workingHours.shiftLabel)))}
        ><Plus class="size-4" aria-hidden="true" />{fa.workingHours.addShift}</Button
      >
      <p class="text-sm text-text-3">{full ? fa.workingHours.maxShifts : fa.workingHours.addShiftHint}</p>
    </div>
  {/if}
  <div aria-live="polite" class="empty:hidden">{#if save.error}<ErrorAlert error={save.error} />{/if}{#if saved && !changed}<Alert tone="success">{fa.workingHours.saved}</Alert>{/if}</div>
  {#if !readOnly}
    <Button type="submit" class="self-start" loading={save.isPending} done={save.isSuccess} disabled={!online.value || !changed || invalid}
      >{fa.app.save}</Button
    >
  {:else}
    <p class="text-sm text-text-3">{fa.workingHours.readOnly}</p>
  {/if}
</form>
